<script>
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import ActionFooter from '@components/footer/action-footer.vue';
import AdminPanel from '@components/admin-panel/admin-panel.vue';
import ClientForm from '@components/forms/client-form.vue';
import AlertBasic from '@components/notifications/alert-basic.vue';
import ExerciseList from '@components/exercise/exercise-list.vue';
import Layout from '@layouts/main.vue';
import HeroProgram from '@components/hero/hero-program.vue';
import moment from 'moment';
import ProgramCalendar from '@components/programs/program-calendar.vue';
import SplitPane from '@components/panels/split-pane.vue';
import SplitPaneFull from '@components/panels/split-pane-full.vue';
import WorkoutDetail from '@components/workout/workout-detail.vue';
import WorkoutList from '@components/workout/workout-list.vue';
import WorkoutPreview from '@components/workout/workout-preview.vue';
import UseLargeScreen from '@components/use-large-screen.vue';
import ProgramNote from '@components/note/program-note.vue';
import { adminPanelMixin } from '@mixins/admin-panel-mixin';
import { helperMixin } from '@mixins/helper-mixin';
import { workoutModelMixin } from '@mixins/models/workout-model-mixin';
import { breakpointsMixin } from '@mixins/breakpoints-mixin.js';
import { cleverTapMixin } from '@mixins/cleverTap-mixin';
import { programMixin } from '@mixins/program-mixin';
import {
  adminPanelComputed,
  adminPanelMethods,
  alertComputed,
  alertMethods,
  programComputed,
  programMethods,
  authComputed,
  clientProgramMethods,
  clientProgramComputed,
  workoutComputed,
  workoutMethods,
  clientComputed,
  trainerMethods,
  exerciseComputed,
  exerciseMethods,
  mediaComputed,
  mediaMethods,
} from '@state/helpers';

export default {
  page: {
    title: 'Program Detail',
    meta: [{ name: 'description', content: 'The Program Detail page.' }],
  },
  props: {
    program: {
      type: Object,
      required: true,
      default: () => {},
    },
  },
  components: {
    ActionFooter,
    AdminPanel,
    AlertBasic,
    ExerciseList,
    HeroProgram,
    Layout,
    ProgramCalendar,
    SplitPane,
    SplitPaneFull,
    WorkoutDetail,
    WorkoutList,
    WorkoutPreview,
    ProgramNote,
    ClientForm,
    UseLargeScreen,
  },
  mixins: [
    adminPanelMixin,
    helperMixin,
    workoutModelMixin,
    programMixin,
    cleverTapMixin,
    breakpointsMixin,
  ],
  data() {
    return {
      activateWeekDuplication: false,
      allProgramInit: null,
      image: require('@assets/images/placeholders/exercise.png'),
      isDeleting: null,
      isSaving: null,
      isTrainerModerating: false,
      lastSaved: null,
      selectedProgramInit: null,
      isClearingWeek: false,
      hasChanges: false,
    };
  },
  computed: {
    ...adminPanelComputed,
    ...alertComputed,
    ...authComputed,
    ...clientComputed,
    ...clientProgramComputed,
    ...exerciseComputed,
    ...mediaComputed,
    ...programComputed,
    ...workoutComputed,
    selectedDayName() {
      if (this.selectedDay && this.selectedDay.dayName) {
        return this.selectedDay.dayName;
      } else if (this.selectedDay && !this.selectedDay.dayName) {
        return this.$_getDayName(this.selectedDay.dayNumber);
      } else {
        return '';
      }
    },
    hasCircuits() {
      return (
        this.selectedWorkout &&
        this.selectedWorkout.circuits &&
        this.selectedWorkout.circuits.length
      );
    },
    isTrainer() {
      return (
        this.roles && this.roles.length > 0 && this.roles[0] === 'isTrainer'
      );
    },
    allowEditing() {
      if (
        (this.program &&
          (!this.isTrainer ||
            (this.isTrainer && this.program.trainer_id !== '0'))) ||
        this.inPanel
      ) {
        return true;
      } else {
        return false;
      }
    },
    panelContentDetail() {
      return (
        (this.panelContent &&
          this.panelContent.data &&
          this.panelContent.data.details) ||
        (this.panelContent &&
          this.panelContent.data &&
          this.panelContent.data.componentRight &&
          this.panelContent.data.componentRight.showPanelDetail)
      );
    },
  },
  async created() {
    await this.fetchProgramRange(this.selectedProgram.programWeeksNumber);
    if (!this.allExercises || !this.allExercises.length) {
      await this.getAllExercises({
        orgId: this.userOrgId,
        trainerId: this.trainerId ? this.trainerId : '0',
      });
    }
  },
  mounted() {
    this.selectedProgramInit = cloneDeep(this.selectedProgram);
    this.allProgramInit = cloneDeep(this.AllClientProgram);
  },
  destroyed() {
    if (this.isNewMediaAsset) {
      this.setIsNewMediaAsset(false);
      this.setClearUploadedMediaStates();
      this.setSignedPostData();
      this.setTempPhotoPreview(null);
    }
    if (this.showAlert) {
      this.setShowAlert();
    }
    // this.$_clearSelections();
  },
  methods: {
    ...adminPanelMethods,
    ...alertMethods,
    ...clientProgramMethods,
    ...exerciseMethods,
    ...mediaMethods,
    ...programMethods,
    ...trainerMethods,
    ...workoutMethods,
    async fetchProgramRange(val) {
      const programId = this.selectedProgram._id;
      const payload = {
        id: programId,
        from: 1,
        to: val,
      };

      if (payload.id && payload.from && payload.to) {
        await this.getProgramRange(payload);
      }
    },
    async showAdminPanel(component, data = null, size = '') {
      if (component === 'ExerciseList') {
        await this.setPanelContent({
          ...this.getPanelContent(component, data, size),
          title: `Day ${this.selectedDay.calculatedDayNumber}: Add New Workout`,
        });
      } else {
        await this.setPanelContent({
          ...this.getPanelContent(component, data, size),
          workoutPreviewType: data.workoutPreviewType
            ? data.workoutPreviewType
            : null,
        });
      }
      this.setShowAdminPanel();
    },
    onHidePanel() {
      // Check changes when panel is not full (on click away from closing the panel and lose changes)
      if (
        (this.hasUnsavedProgramChanges || this.hasUnsavedChanges) &&
        !this.panelContentDetail
      ) {
        this.$router.app.$bvModal.show('unsaved-changes-modal');
      } else {
        if (this.isTrainerModerating) {
          this.setIsLibraryWorkout(false);
          this.setInitialLibraryWorkout(null);

          if (this.panelContent.data.componentRight.showPanelDetail) {
            this.setHidePanelContentDetail();
          } else {
            this.setSelectedWorkout(null);
          }
        } else {
          this.setSelectedWorkout(null);
        }
      }
    },
    async onAddWorkout(day, week) {
      this.isTrainerModerating = true;
      this.setIsLibraryWorkout(false);
      this.setInitialLibraryWorkout(null);
      const workout = await this.getWorkoutModel();
      workout.isCustom = true;
      workout.programWorkout_id = null;
      workout.trainer_id = this.trainerId;
      workout.org_id = this.userOrgId;
      await this.setSelectedWorkout(workout);
      await this.setSelectedWeek(week);
      await this.setSelectedDay(day);

      const panelContent = {
        panelTitle: 'Create Workout',
        componentLeft: {
          name: 'WorkoutDetail',
          selectedWorkout: this.selectedWorkout,
          isNewWorkout: true,
          inPanel: true,
        },
        componentRight: {
          name: null,
          hideDescription: true,
          showExerciseFilters: true,
        },
      };
      this.showAdminPanel('SplitPaneFull', panelContent, 'split-lg');
    },
    assignClient() {
      this.setHideAdminPanel();
      this.$bvModal.show('item-select-modal');
    },
    async onViewWorkout(workout, day, week) {
      this.isTrainerModerating = true;
      this.setIsLibraryWorkout(false);
      this.setInitialLibraryWorkout(null);
      const dayId = day.programDay_id;
      const trainerId = week.trainer_id;
      const orgId = week.org_id;
      const userId = week.user_id;
      const payload = { dayId, trainerId, orgId, userId };

      if (dayId && trainerId && orgId && userId) {
        await this.getAllDay(payload)
          .then((res) => {
            if (res && res.success && res.programDay.workouts.length > 0) {
              const workout = res.programDay.workouts[0];
              this.setSelectedWorkout(workout);
              this.setSelectedWeek(week);
              this.setSelectedDay(res.programDay);
              const panelContent = {
                panelTitle: this.allowEditing ? 'Edit Workout' : 'View Workout',
                componentLeft: {
                  name: 'WorkoutDetail',
                  selectedWorkout: this.selectedWorkout,
                  inPanel: true,
                },
                componentRight: {
                  name: 'ExerciseList',
                  hideDescription: true,
                  showExerciseFilters: true,
                },
              };
              this.showAdminPanel('SplitPaneFull', panelContent, 'split-lg');
            }
          })
          .catch((e) => console.log(e));
      }
    },
    async onChangeProgramWeeks(val) {
      await this.fetchProgramRange(val);
      await this.setSelectedProgram({
        ...this.selectedProgram,
        programWeeksNumber: val,
      });
    },
    setActivateWeekDuplication(val) {
      this.activateWeekDuplication = val;
    },
    deselectWeek() {
      if (this.selectedAllWeek && this.selectedAllWeek.weekOf) {
        const prevCheck = document.getElementById(this.selectedAllWeek.weekOf);
        prevCheck.checked = false;
      }
      this.setSelectedAllWeek(null);
      this.setActivateWeekDuplication(false);
    },
    async onClearAllWeekEvents() {
      this.isClearingWeek = true;
      const payload = {
        programId: this.selectedAllWeek.program_id,
        weekId: this.selectedAllWeek._id,
      };
      await this.clearAllWeekEvents(payload)
        .then((res) => {
          this.deselectWeek();
          this.isClearingWeek = false;
        })
        .catch((e) => console.log(e));
    },
    async onSaveProgram() {
      this.isSaving = true;
      const programPayload = {
        programId: this.selectedProgram._id,
        orgId: this.selectedProgram.org_id,
        trainerId: this.selectedProgram.trainer_id,
        userId: this.selectedProgram.user_id,
        program: this.selectedProgram,
      };

      delete programPayload.program.programWorkoutsNumber;

      if (this.hasNewMedia('cover', 'programs')) {
        programPayload.program.mediaAssets.coverPhoto.url = this.hasNewMedia(
          'cover',
          'programs'
        ).publicUrl;
      }

      if (
        programPayload.programId &&
        programPayload.trainerId &&
        programPayload.userId &&
        programPayload.program
      ) {
        await this.updateProgram(programPayload);
        if (this.allPrograms && this.allPrograms.length > 0) {
          await this.setUpdateProgramItem(this.selectedProgram);
          this.lastSaved = `${moment().format('MMMM Do')} at ${moment().format(
            'hh:mm a'
          )}`;
          this.setHasUnsavedProgramChanges(false);
        }
        this.selectedProgramInit = cloneDeep(this.selectedProgram);
        this.allProgramInit = cloneDeep(this.AllClientProgram);

        this.isSaving = false;
      }
    },
    async onDeleteProgram() {
      this.isDeleting = true;
      const programPayload = {
        programId: this.selectedProgram._id,
        orgId: this.selectedProgram.org_id,
        trainerId: this.selectedProgram.trainer_id,
        userId: this.selectedProgram.user_id,
        program: this.selectedProgram,
      };
      if (
        programPayload.programId &&
        programPayload.trainerId &&
        programPayload.userId &&
        programPayload.program
      ) {
        await this.deleteProgram(programPayload)
          .then(async (res) => {
            if (res.success) {
              await this.$router.push({ name: 'programs' }).catch(()=>{});
              this.setSelectedProgram(null);
              this.setAllClientProgram([]);
              this.setHasUnsavedProgramChanges(false);
              if (
                this.currentUser &&
                this.currentUser.roles &&
                this.currentUser.roles.isTrainer &&
                programPayload.programId
              ) {
                const program = programPayload.programId;
                const trainer = this.trainerEmail;
                this.$_pushCleverTapEvent('Delete Program', {
                  program,
                  trainer,
                });
              }
            }
          })
          .catch((e) => console.log(e));
        this.isDeleting = false;
      }
    },
    async toClient() {
      if (this.selectedClient) {
        this.$router.push({
          name: 'clientProgram',
          params: { id: this.selectedClient._id, client: this.selectedClient },
        }).catch(()=>{});
      }
    },
    cancelChanges() {
      this.setSelectedProgram(this.selectedProgramInit);
      this.onChangeProgramWeeks(this.selectedProgram.programWeeksNumber);
      this.setHasUnsavedProgramChanges(false);
      if (this.isNewMediaAsset) {
        this.setIsNewMediaAsset(false);
        this.setClearUploadedMediaStates();
        this.setSignedPostData();
        this.setTempPhotoPreview(null);
      }
    },
    showWorkoutListPanelContent(component) {
      this.setPanelContent({
        ...this.panelContent,
        data: {
          ...this.panelContent.data,
          panelTitle: `Add Workout to ${this.selectedDayName}`,
          componentRight: {
            name: component,
            showPanelDetail: true,
            showWorkoutFilters: true,
          },
        },
      });
    },
    async expandPanel(component, selectedCircuitIndex = null) {
      switch (component) {
        case 'WorkoutList':
          this.showWorkoutListPanelContent(component);
          break;
        default:
          break;
      }
    },
    async onDuplicateProgram(program) {
      const label = 'Copy'.concat(' ', program.label);
      const payload = {
        id: program._id,
        label: label,
      };
      await this.duplicateProgram(payload).then((res) => {
        if (res && res.status === 200) {
          this.setSelectedProgram(res.data.program);
          this.toProgramDetail(this.selectedProgram._id, this.selectedProgram);
          if (
            this.currentUser &&
            this.currentUser.roles &&
            this.currentUser.roles.isTrainer &&
            res &&
            res.data.program
          ) {
            const program = res.data.program._id;
            const trainer = this.trainerEmail;
            this.$_pushCleverTapEvent('Create Program', { program, trainer });
          }
        }
      });
    },
    toProgramDetail(programId, program) {
      this.$router.push({
        name: 'programDetail',
        params: { id: programId, program: program },
      }).catch(()=>{});
    },
  },
  watch: {
    async isAdminPanelActive(n, o) {
      if (
        !n &&
        this.panelContent &&
        this.panelContent.component === 'ClientForm'
      ) {
        this.setPanelContent(null);
        this.assignClient();
      }
    },
    selectedProgram(n, o) {
      this.hasChanges = !isEqual(
        this.selectedProgram,
        this.selectedProgramInit
      );

      this.setHasUnsavedProgramChanges(this.hasChanges);
    },
  },
};
</script>

<template>
  <UseLargeScreen v-if="isSmallScreenView" />
  <Layout v-else-if="selectedProgram">
    <AlertBasic v-if="selectedClient && selectedClient.profile">
      <span class="alert-msg">
        A copy of this program was just created within
        {{
          `${selectedClient.profile.firstName} ${selectedClient.profile.lastName}`
        }}'s profile. You are still editing the template, which will not affect
        the copy just created.
      </span>
      <a @click="toClient"
        >Go to {{ selectedClient.profile.firstName }}'s program now.</a
      >
    </AlertBasic>

    <AdminPanel
      v-if="!isTrainerModerating && panelContent && panelContent.component"
      :title="panelContent ? panelContent.title : ''"
      :size="panelContent ? panelContent.size : ''"
      :noPad="
        panelContent && panelContent.component === 'ClientForm' ? false : true
      "
    >
      <SplitPane
        v-if="panelContent && panelContent.component === 'ExerciseList'"
      >
        <span
          v-if="panelContent && panelContent.data && !panelContent.data.details"
          class="add-workout"
        >
          <span class="action">Add Workout</span>
          <img
            class="chevron-right"
            src="@assets/images/icon-chevron-right.svg"
          />
        </span>
      </SplitPane>
      <WorkoutPreview
        v-if="panelContent && panelContent.component === 'WorkoutPreview'"
        :mini="true"
      />
      <ProgramNote
        :noteItem="noteItem"
        @delete-note="1"
        @update-create-note="createNote"
        v-if="panelContent && panelContent.component === 'ProgramNote'"
      />
      <ClientForm
        v-if="panelContent && panelContent.component === 'ClientForm'"
      />
    </AdminPanel>

    <AdminPanel
      v-if="isTrainerModerating"
      :title="panelContent ? panelContent.title : ''"
      :size="panelContent ? panelContent.size : ''"
      :split="
        panelContent && panelContent.data && panelContent.data.split === true
      "
      :noPad="true"
      @hide-panel="onHidePanel"
    >
      <template
        v-slot:header
        v-if="
          allowEditing &&
            !hasCircuits &&
            panelContent &&
            panelContent.data &&
            panelContent.data.componentLeft &&
            panelContent.data.componentLeft.name === 'WorkoutDetail'
        "
      >
        <span class="workout-btn">
          <span>or</span>
          <BaseButton
            type="button"
            class="btn-secondary btn-round"
            @click="expandPanel('WorkoutList')"
          >
            Start From Workout Library
          </BaseButton>
        </span>
      </template>

      <component
        v-if="panelContent"
        :is="panelContent.component"
        :content="panelContent"
        :workout="selectedWorkout"
        :hideDescription="true"
        :showExerciseFilters="panelContent.data.showExerciseFilters"
        :inPanel="true"
      />
    </AdminPanel>

    <div class="container-wrapper full">
      <HeroProgram
        :title="selectedProgram.label"
        :description="selectedProgram.description"
        @on-change-program-weeks="onChangeProgramWeeks"
        :allowEditing="allowEditing"
      />

      <ProgramCalendar
        :isClientProgram="false"
        @add-workout="onAddWorkout"
        @view-workout="onViewWorkout"
        @set-activate-week-duplication="setActivateWeekDuplication"
        :activateWeekDuplication="activateWeekDuplication"
        :programWeeks="AllClientProgram"
        :allowEditing="allowEditing"
      />
      <ActionFooter :color="selectedAllWeek ? 'blue' : 'black'">
        <span
          v-if="!selectedAllWeek && lastSaved && !hasChanges && allowEditing"
          class="saved-status"
          slot="actionState"
        >
          Last Saved: {{ lastSaved }}
        </span>
        <span
          v-if="!selectedAllWeek && !hasChanges && !lastSaved && allowEditing"
          class="saved-status"
          slot="actionState"
          >No changes</span
        >
        <span
          v-if="!selectedAllWeek && hasChanges && allowEditing"
          slot="secondaryAction"
        >
          Not saved
          <span class="link-deselect" @click="cancelChanges">
            Cancel
          </span>
        </span>
        <span v-if="selectedAllWeek && allowEditing" slot="secondaryAction">
          Week {{ selectedAllWeek.weekNumber }}
          <span class="link-deselect" @click="deselectWeek">
            Deselect
          </span>
        </span>
        <span v-if="!allowEditing" class="saved-status" slot="actionState"
          >Duplicate to make changes</span
        >
        <BaseButton
          v-if="!selectedAllWeek && allowEditing"
          type="button"
          class="btn-primary btn-clear"
          @click="onDeleteProgram"
        >
          <svg width="14" height="18" viewBox="0 0 14 18" fill="none" xmlns="http://www.w3.org/2000/svg" v-if="!isDeleting" v-bind:class="'icon-trash'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M10.5 1H14v2H0V1h3.5l1-1h5l1 1zM3 18c-1.1 0-2-.9-2-2V4h12v12c0 1.1-.9 2-2 2H3z" fill="#000"/></svg>
          <BaseIcon v-if="isDeleting" name="sync" spin class="icon-trash" />
          Delete Program
        </BaseButton>
        <span
          v-if="!selectedAllWeek && trainerId && allowEditing"
          class="divider-vertical"
          >&nbsp;</span
        >
        <BaseButton
          :style="{ opacity: hasChanges ? 0.6 : 1 }"
          :disabled="hasChanges"
          v-if="!selectedAllWeek && trainerId"
          type="button"
          class="btn-primary btn-clear"
          @click="assignClient"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon-user'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M3 5v14a2 2 0 002 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2H5a2 2 0 00-2 2zm12 4c0 1.66-1.34 3-3 3s-3-1.34-3-3 1.34-3 3-3 3 1.34 3 3zm-3 4.9c-2 0-6 1.1-6 3.1v1h12v-1c0-2-4-3.1-6-3.1z" fill="#fff"/></svg>
          Assign to Client
        </BaseButton>
        <span class="divider-vertical">&nbsp;</span>
        <BaseButton
          @click="onDuplicateProgram(program)"
          type="button"
          class="btn-primary btn-clear"
          v-if="!allowEditing"
        >
          <svg width="22" height="26" viewBox="0 0 22 26" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon-save'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M15.876.4H2.296C1.053.4.034 1.43.034 2.69v16.037h2.264V2.691h13.579V.4zm3.394 4.582H6.823c-1.245 0-2.263 1.03-2.263 2.29V23.31c0 1.26 1.018 2.291 2.263 2.291H19.27c1.245 0 2.264-1.03 2.264-2.29V7.272c0-1.26-1.019-2.291-2.264-2.291zM6.823 23.309H19.27V7.273H6.823v16.036z" fill="#2F70DE"/></svg>
          Duplicate
        </BaseButton>
        <BaseButton
          @click="onSaveProgram"
          type="button"
          class="btn-primary btn-clear"
          :style="{ opacity: !hasChanges ? 0.6 : 1 }"
          v-if="!selectedAllWeek && allowEditing"
          :disabled="!hasChanges"
        >
          <BaseIcon v-if="!isSaving" name="save" />
          <BaseIcon v-if="isSaving" name="sync" spin />
          Save Changes
        </BaseButton>
        <BaseButton
          v-if="selectedAllWeek"
          type="button"
          class="btn-primary btn-clear"
          @click="onClearAllWeekEvents"
        >
          <BaseIcon v-if="isClearingWeek" name="sync" spin class="icon-trash" />
          <svg width="14" height="18" viewBox="0 0 14 18" fill="none" xmlns="http://www.w3.org/2000/svg" v-if="!isClearingWeek" v-bind:class="'btn-icon icon-trash'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M10.5 1H14v2H0V1h3.5l1-1h5l1 1zM3 18c-1.1 0-2-.9-2-2V4h12v12c0 1.1-.9 2-2 2H3z" fill="#000"/></svg>
          Clear Week
        </BaseButton>
        <span v-if="selectedAllWeek" class="divider-vertical">&nbsp;</span>
        <BaseButton
          v-if="selectedAllWeek"
          type="button"
          class="btn-primary btn-clear"
          @click="setActivateWeekDuplication(true)"
        >
          <svg width="22" height="26" viewBox="0 0 22 26" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'btn-icon icon-copy'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M15.876.4H2.296C1.053.4.034 1.43.034 2.69v16.037h2.264V2.691h13.579V.4zm3.394 4.582H6.823c-1.245 0-2.263 1.03-2.263 2.29V23.31c0 1.26 1.018 2.291 2.263 2.291H19.27c1.245 0 2.264-1.03 2.264-2.29V7.272c0-1.26-1.019-2.291-2.264-2.291zM6.823 23.309H19.27V7.273H6.823v16.036z" fill="#2F70DE"/></svg>
          Duplicate Week
        </BaseButton>
      </ActionFooter>
    </div>
  </Layout>
</template>

<style lang="scss" scoped>
@import '@design';
// stylelint-disable selector-pseudo-element-no-unknown

.container-wrapper {
  display: block !important;
  padding-bottom: 17rem;
}

::v-deep {
  .alert {
    a {
      margin-left: 3px;
      margin-right: 2px;
    }
  }

  .action-footer-wrapper {
    bottom: 4rem;
    left: 3rem;
    padding-right: 6rem;
    z-index: 4;
  }

  .workout-preview-content {
    padding-bottom: 15rem !important;
  }

  .workout-preview {
    padding-bottom: 10rem;

    .panel-footer {
      bottom: 2rem;
    }
  }

  .pane-right {
    padding-bottom: 10rem !important;
  }

  form .new-client {
    bottom: 5rem;
  }

  .add-new-workout-wrapper {
    background-color: white;

    p {
      padding-bottom: 0;
    }
  }
}
</style>
